/**
 * Nachweishistorie im Nachweis-Detail (ADR 007): Statusübergänge und Änderungen an Terminen und
 * Beteiligten, neueste zuerst – je Eintrag wer, wann, was und der Kommentar. Die Antragshistorie
 * zeigt sich genauso, nur ohne Status (`PermitApplicationsPage`).
 *
 * Als kompaktes Protokoll in festen Spalten: Zeitpunkt, Benutzer, Aktion → Status, Änderungen.
 * Die Änderungen stehen als kleine Tabelle (Feld, alt → neu) mit festen Breiten, damit die
 * Pfeile über alle Einträge hinweg untereinander stehen. Gezeigt werden die ersten zwei; der
 * Rest klappt auf, solange man über dem Eintrag steht. Nur ein Kommentar bekommt eine eigene
 * Zeile. Freitext wie die Notiz bleibt einzeilig, der ganze Text steht im Tooltip.
 *
 * Lädt neu, sobald sich die Quelle ändert: Nach einem Übergang oder Speichern liefert der
 * Server einen neuen Nachweis bzw. Antrag, und mit ihm ist ein neuer Eintrag entstanden.
 */
import React, { useEffect, useRef, useState } from "react";
import { Box, Collapse, Tooltip, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";
import type { SxProps, Theme } from "@mui/material/styles";
import StatusChip from "../../../common/display/StatusChip";
import { EvidenceHistorieEintrag } from "../../../../types";
import { colors, fonts } from "../../../../styles/theme";
import { formatCurrency } from "../../../../utils/formatters";
import { getEvidenceStatusVisual } from "../../../../utils/colors/evidenceStatusUtils";

interface EvidenceHistorieProps {
  /** Wessen Historie – ändert sie sich, lädt die Historie neu */
  source: unknown;
  ladeHistorie: () => Promise<EvidenceHistorieEintrag[]>;
}

const ISO_DATUM = /^\d{4}-\d{2}-\d{2}$/;

/** Breite der Spalte mit Punkt und Linie */
const PUNKT_SPALTE = 12;
/** Höhe der ersten Zeile eines Eintrags – so hoch wie der Status-Chip, der Punkt sitzt mittig */
const ZEILE = 24;
/** Innenabstand eines Eintrags in px – Platz für den Rahmen beim Überfahren */
const INNEN_X = 8;
const INNEN_Y = 6;
/** Abstand zwischen zwei Einträgen in px */
const ABSTAND = 4;
/** Änderungen, die ohne Überfahren zu sehen sind */
const SICHTBARE_AENDERUNGEN = 2;

/** Feld | alt | → | neu – in jeder Zeile gleich, damit die Pfeile fluchten */
const AENDERUNG_SPALTEN = "180px 88px 16px minmax(0, 1fr)";

/** Beträge der Kostenposition – der Server hält sie als Zahl fest, die Oberfläche formatiert */
const AMOUNT_FIELDS = new Set(["betrag_geplant", "betrag_ist"]);

/** Werte kommen als Anzeigetext; Datumswerte stehen als ISO-Datum und werden deutsch gezeigt */
const wert = (feld: string, text?: string | null): string => {
  if (!text) return "–";
  if (AMOUNT_FIELDS.has(feld)) return formatCurrency(Number(text));
  return ISO_DATUM.test(text) ? format(parseISO(text), "dd.MM.yyyy") : text;
};

/** Immer zweistellig – sonst springt die Spalte zwischen 9.10. und 10.10. */
const zeitpunkt = (iso: string): string =>
  // Der Server speichert UTC ohne Zonenangabe.
  format(new Date(iso.endsWith("Z") ? iso : `${iso}Z`), "dd.MM.yyyy, HH:mm");

/** Freitext wie die Notiz steht in einer Zeile; der ganze Text mit Umbrüchen im Tooltip */
const FREITEXT_FELDER = new Set(["note"]);

const Wert: React.FC<{
  feld: string;
  text?: string | null;
  sx: SxProps<Theme>;
}> = ({ feld, text, sx }) => {
  const anzeige = wert(feld, text);
  if (FREITEXT_FELDER.has(feld) && text) {
    return (
      <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{text}</Box>}>
        <Typography variant="caption" noWrap sx={sx}>
          {anzeige}
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Typography variant="caption" noWrap sx={sx} title={anzeige}>
      {anzeige}
    </Typography>
  );
};

const zelleSx = {
  minHeight: ZEILE,
  display: "flex",
  alignItems: "center",
  minWidth: 0,
} as const;

const AenderungsZeilen: React.FC<{
  aenderungen: EvidenceHistorieEintrag["aenderungen"];
}> = ({ aenderungen }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: AENDERUNG_SPALTEN,
      columnGap: 1,
      alignItems: "center",
    }}
  >
    {aenderungen.map((aenderung) => (
      <React.Fragment key={aenderung.feld}>
        <Typography
          variant="caption"
          noWrap
          sx={{ color: colors.text.secondary }}
          title={aenderung.label}
        >
          {aenderung.label}
        </Typography>
        {/* Ohne Recht an den Kosten: nur dass sie sich geändert haben, keine Beträge */}
        {aenderung.hidden ? (
          <span />
        ) : (
          <Wert
            feld={aenderung.feld}
            text={aenderung.alt}
            sx={{ textAlign: "right", color: colors.text.secondary }}
          />
        )}
        <Typography
          variant="caption"
          sx={{ textAlign: "center", color: colors.text.secondary }}
        >
          {aenderung.hidden ? "" : "→"}
        </Typography>
        {aenderung.hidden ? (
          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
            geändert
          </Typography>
        ) : (
          <Wert
            feld={aenderung.feld}
            text={aenderung.neu}
            sx={{ color: colors.text.primary }}
          />
        )}
      </React.Fragment>
    ))}
  </Box>
);

const EvidenceHistorie: React.FC<EvidenceHistorieProps> = ({
  source,
  ladeHistorie,
}) => {
  // Geladen wird nur, wenn sich die Quelle ändert – nicht bei jeder neuen Ladefunktion.
  const load = useRef(ladeHistorie);
  load.current = ladeHistorie;
  const [eintraege, setEintraege] = useState<EvidenceHistorieEintrag[]>([]);
  /** Eintrag unter der Maus – er bekommt den Rahmen und klappt seine übrigen Änderungen auf */
  const [ueberfahren, setUeberfahren] = useState<string | null>(null);

  useEffect(() => {
    let aktuell = true;
    load
      .current()
      .then((geladen) => {
        if (aktuell) setEintraege(geladen);
      })
      // Die Historie ist eine Zugabe zum Detail; ohne sie bleibt der Abschnitt einfach leer.
      .catch(() => {
        if (aktuell) setEintraege([]);
      });
    return () => {
      aktuell = false;
    };
  }, [source]);

  if (eintraege.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{ color: colors.text.secondary, fontStyle: "italic" }}
      >
        Noch keine Einträge
      </Typography>
    );
  }

  return (
    <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {eintraege.map((eintrag, index) => {
        const letzter = index === eintraege.length - 1;
        const offen = ueberfahren === eintrag.id;
        const ersteAenderungen = eintrag.aenderungen.slice(
          0,
          SICHTBARE_AENDERUNGEN,
        );
        const weitere = eintrag.aenderungen.slice(SICHTBARE_AENDERUNGEN);
        return (
          <Box
            component="li"
            key={eintrag.id}
            onMouseEnter={() => setUeberfahren(eintrag.id)}
            onMouseLeave={() =>
              setUeberfahren((bisher) =>
                bisher === eintrag.id ? null : bisher,
              )
            }
            sx={{
              position: "relative",
              display: "grid",
              // Breit eine Zeile je Eintrag in festen Spalten: Punkt, Zeitpunkt, Benutzer,
              // Aktion, Pfeil, Status, Änderungen. Schmal stehen dieselben Angaben
              // untereinander neben dem Punkt.
              gridTemplateColumns: {
                xs: `${PUNKT_SPALTE}px 1fr`,
                md: `${PUNKT_SPALTE}px 124px 112px 150px 16px 150px 1fr`,
              },
              columnGap: 2,
              alignItems: "start",
              px: `${INNEN_X}px`,
              py: `${INNEN_Y}px`,
              mb: letzter ? 0 : `${ABSTAND}px`,
              border: "1px solid transparent",
              borderRadius: 1.5,
              transition:
                "border-color 160ms ease, background-color 160ms ease",
              "&:hover": {
                borderColor: colors.border.light,
                backgroundColor: colors.background.default,
              },
              // Die Linie verbindet die Punkte über den Abstand hinweg; am letzten endet sie.
              "&::before": letzter
                ? undefined
                : {
                    content: '""',
                    position: "absolute",
                    left: INNEN_X + PUNKT_SPALTE / 2 - 0.5,
                    top: INNEN_Y + ZEILE / 2,
                    bottom: -(2 + ABSTAND + INNEN_Y + ZEILE / 2),
                    borderLeft: `1px solid ${colors.border.light}`,
                  },
            }}
          >
            <Box
              sx={{
                ...zelleSx,
                gridRow: { xs: "1 / span 6", md: "1" },
                justifyContent: "center",
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: 11,
                  height: 11,
                  borderRadius: "50%",
                  backgroundColor: eintrag.status_nach
                    ? getEvidenceStatusVisual(eintrag.status_nach).color
                    : colors.border.medium,
                }}
              />
            </Box>
            <Box sx={zelleSx}>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: colors.text.secondary,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {zeitpunkt(eintrag.erstellt_am)}
              </Typography>
            </Box>
            <Box sx={zelleSx}>
              <Typography
                variant="caption"
                noWrap
                sx={{ color: colors.text.secondary }}
                title={eintrag.benutzer ?? undefined}
              >
                {eintrag.benutzer ?? "–"}
              </Typography>
            </Box>
            <Box sx={zelleSx}>
              <Typography
                variant="body2"
                noWrap
                sx={{ fontWeight: fonts.weights.semiBold }}
                title={eintrag.aktion_label}
              >
                {eintrag.aktion_label}
              </Typography>
            </Box>
            {/* Aktion → Ziel: nur bei einem Statuswechsel */}
            <Box sx={{ ...zelleSx, justifyContent: "center" }}>
              {eintrag.status_nach && (
                <Typography
                  variant="body2"
                  sx={{ color: colors.text.secondary }}
                >
                  →
                </Typography>
              )}
            </Box>
            <Box sx={zelleSx}>
              {eintrag.status_nach && (
                <StatusChip
                  size="small"
                  {...getEvidenceStatusVisual(eintrag.status_nach)}
                />
              )}
            </Box>
            <Box sx={{ minWidth: 0, pt: "3px" }}>
              <AenderungsZeilen aenderungen={ersteAenderungen} />
              {weitere.length > 0 && (
                <>
                  <Collapse in={offen} timeout={200}>
                    <AenderungsZeilen aenderungen={weitere} />
                  </Collapse>
                  <Collapse in={!offen} timeout={200}>
                    <Typography
                      variant="caption"
                      sx={{ color: colors.text.secondary, fontStyle: "italic" }}
                    >
                      +{weitere.length} weitere
                    </Typography>
                  </Collapse>
                </>
              )}
            </Box>
            {eintrag.kommentar && (
              <Typography
                variant="body2"
                sx={{
                  // Breit unter Aktion, Status und Änderungen, schmal unter den übrigen Angaben.
                  gridColumn: { xs: "2", md: "4 / -1" },
                  mt: 0.5,
                  whiteSpace: "pre-wrap",
                  color: colors.text.primary,
                }}
              >
                {eintrag.kommentar}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default EvidenceHistorie;
